import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { api } from '../lib/api';
import { useToast } from '../context/ToastContext';
import CampoSenha from '../components/CampoSenha';

// Contas do WhatsLight (bot.usuarios) — os logins que o app desktop e a extensão Chrome usam. São SEPARADAS dos
// usuários do painel. Só superadmin (menu e API). Aqui dá para criar uma conta, trocar a senha e
// ativar/bloquear. O login (e-mail) não é editável: ele é referência de configuração e das credenciais salvas
// nos apps — para trocar, crie uma conta nova.
//
// BOT GROUP por usuário: uma conta BOT GROUP pode ser VINCULADA a um usuário do painel. Vinculada, ela só entra na
// extensão se o usuário tem a permissão "Usar o BOT GROUP" (Usuários → permissões) E a empresa tem o recurso
// liberado (Empresas → recursos); sem vínculo, entra sem checar permissão (compatibilidade até alguém vincular).
const TIPOS_USO = {
  bot: ['BOT', 'primary', 'App desktop — contatos individuais (clientes e corretores)'],
  grupo: ['BOT GROUP', 'info', 'Extensão do Chrome — grupos do WhatsApp'],
};
const SITUACOES = { 0: ['Inativa', 'secondary'], 1: ['Ativa', 'success'], 2: ['Bloqueada', 'danger'] };

export default function ContasWhatsLight() {
  const toast = useToast();
  const [carregando, setCarregando] = useState(true);
  const [contas, setContas] = useState([]);
  const [imobiliarias, setImobiliarias] = useState([]);
  const [modal, setModal] = useState(null); // null | { conta: objeto|null }
  const [usuariosPainel, setUsuariosPainel] = useState([]);
  const [form, setForm] = useState({ nome: '', email: '', senha: '', status: 1, tipoUso: '', imobiliariaId: '', usuarioPublicId: '' });
  const [salvando, setSalvando] = useState(false);

  function carregar() {
    api
      .get('/whatslight/contas')
      .then((r) => {
        setContas(r.data);
        setImobiliarias(r.imobiliarias || []);
      })
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar as contas'))
      .finally(() => setCarregando(false));
  }
  useEffect(carregar, []); // eslint-disable-line react-hooks/exhaustive-deps
  // usuários do painel para o vínculo do BOT GROUP (recarrega ao abrir o modal: a permissão pode ter mudado)
  function carregarUsuariosPainel() {
    api.get('/whatslight/usuarios-painel').then((r) => setUsuariosPainel(r.data)).catch(() => {});
  }

  function abrirNova() {
    setForm({ nome: '', email: '', senha: '', status: 1, tipoUso: '', imobiliariaId: imobiliarias[0]?.id ?? '', usuarioPublicId: '' });
    carregarUsuariosPainel();
    setModal({ conta: null });
  }
  function abrirEdicao(conta) {
    setForm({ nome: conta.nome, email: conta.email, senha: '', status: conta.status, tipoUso: conta.tipoUso ?? '', imobiliariaId: conta.imobiliariaId ?? '', usuarioPublicId: conta.usuarioPainel?.publicId ?? '' });
    carregarUsuariosPainel();
    setModal({ conta });
  }

  async function salvar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      if (modal.conta) {
        const corpo = { nome: form.nome, status: Number(form.status), tipoUso: form.tipoUso || null };
        // vínculo só existe no BOT GROUP; vazio = desvincular
        if (form.tipoUso === 'grupo') corpo.usuarioPublicId = form.usuarioPublicId || null;
        if (form.senha) corpo.senha = form.senha; // em branco = mantém a senha atual
        await api.put(`/whatslight/contas/${modal.conta.id}`, corpo);
        toast.sucesso(form.senha ? 'Conta atualizada e senha trocada.' : 'Conta atualizada.');
      } else {
        await api.post('/whatslight/contas', {
          nome: form.nome,
          email: form.email,
          senha: form.senha,
          tipoUso: form.tipoUso,
          imobiliariaId: form.imobiliariaId,
          ...(form.tipoUso === 'grupo' && form.usuarioPublicId ? { usuarioPublicId: form.usuarioPublicId } : {}),
        });
        toast.sucesso('Conta criada.');
      }
      setModal(null);
      carregar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar');
    } finally {
      setSalvando(false);
    }
  }

  const campo = (nome) => ({ value: form[nome], onChange: (e) => setForm((f) => ({ ...f, [nome]: e.target.value })) });
  const nova = modal && !modal.conta;
  const usuarioEscolhido = usuariosPainel.find((u) => u.publicId === form.usuarioPublicId);

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="h4 mb-0">Contas do WhatsLight</h1>
        <Button variant="primary" size="sm" onClick={abrirNova} disabled={carregando}>
          <i className="bi bi-plus-lg" /> Nova conta
        </Button>
      </div>
      <Alert variant="light" className="small">
        São os logins que o app desktop (o bot) e a extensão do Chrome usam para conectar ao WhatsLight — separados dos
        usuários do painel. O <strong>tipo de uso</strong> define o que cada conta faz: toda conta <strong>ativa do tipo BOT</strong>
        opera o bot de cadastro dos corretores (os vínculos valem para qualquer uma delas, então dá para trocar a conta do app sem
        desvincular ninguém); contas BOT GROUP são da extensão do Chrome.
      </Alert>

      {carregando ? (
        <Spinner animation="border" />
      ) : (
        <Table responsive hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Login</th>
              <th>Tipo de uso</th>
              <th>Usuário do painel (BOT GROUP)</th>
              <th>Imobiliária</th>
              <th>Situação</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {contas.map((c) => {
              const [rotulo, cor] = SITUACOES[c.status] || ['?', 'secondary'];
              return (
                <tr key={c.id}>
                  <td>{c.nome}</td>
                  <td>{c.email}</td>
                  <td>
                    {TIPOS_USO[c.tipoUso]
                      ? <Badge bg={TIPOS_USO[c.tipoUso][1]} title={TIPOS_USO[c.tipoUso][2]}>{TIPOS_USO[c.tipoUso][0]}</Badge>
                      : <span className="text-muted small" title="Conta antiga: funciona nos dois modos até você definir">Não definido</span>}
                  </td>
                  <td>
                    {c.tipoUso !== 'grupo' ? (
                      <span className="text-muted">—</span>
                    ) : c.usuarioPainel ? (
                      <>
                        {c.usuarioPainel.nome}
                        {c.usuarioPainel.empresa ? <span className="text-muted small"> · {c.usuarioPainel.empresa}</span> : null}{' '}
                        <Badge bg={c.usuarioPainel.botGroup ? 'success' : 'warning'} text={c.usuarioPainel.botGroup ? undefined : 'dark'}>
                          {c.usuarioPainel.botGroup ? 'liberado' : 'sem permissão'}
                        </Badge>
                      </>
                    ) : (
                      <span className="text-muted small" title="Sem vínculo, a conta entra na extensão sem checar permissão">Sem vínculo (livre)</span>
                    )}
                  </td>
                  <td>{c.imobiliariaCodigo ? `Código ${c.imobiliariaCodigo}` : '—'}</td>
                  <td><Badge bg={cor}>{rotulo}</Badge></td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" onClick={() => abrirEdicao(c)}>
                      Editar / trocar senha
                    </Button>
                  </td>
                </tr>
              );
            })}
            {contas.length === 0 && (
              <tr><td colSpan={7} className="text-muted">Nenhuma conta cadastrada.</td></tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={!!modal} onHide={() => setModal(null)} centered>
        <Form onSubmit={salvar}>
          <Modal.Header closeButton>
            <Modal.Title>{nova ? 'Nova conta do WhatsLight' : 'Editar conta'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="wl-nome">
              <Form.Label>Nome</Form.Label>
              <Form.Control {...campo('nome')} required autoFocus />
            </Form.Group>
            <Form.Group className="mb-3" controlId="wl-email">
              <Form.Label>Login (e-mail)</Form.Label>
              <Form.Control {...campo('email')} required disabled={!nova} autoComplete="off" />
              {!nova && <Form.Text muted>O login não pode ser alterado. Para trocar, crie uma conta nova.</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3" controlId="wl-tipo">
              <Form.Label>Tipo de uso</Form.Label>
              <Form.Select {...campo('tipoUso')} required={nova}>
                {nova && <option value="">Selecione…</option>}
                {!nova && <option value="">Não definido (funciona nos dois modos)</option>}
                <option value="bot">BOT — app desktop (contatos individuais)</option>
                <option value="grupo">BOT GROUP — extensão do Chrome (grupos)</option>
              </Form.Select>
              <Form.Text muted>
                Uma conta só funciona no tipo escolhido: a conta BOT não consegue entrar na extensão, e a BOT GROUP não recebe a fila do app desktop.
              </Form.Text>
            </Form.Group>
            {form.tipoUso === 'grupo' && (
              <Form.Group className="mb-3" controlId="wl-usuario-painel">
                <Form.Label>Usuário do painel</Form.Label>
                <Form.Select {...campo('usuarioPublicId')}>
                  <option value="">Sem vínculo (a conta entra sem checar permissão)</option>
                  {usuariosPainel.map((u) => (
                    <option key={u.publicId} value={u.publicId}>
                      {u.nome}{u.empresa ? ` — ${u.empresa}` : ''} ({u.email}){u.botGroup ? ' ✓ liberado' : ' · sem permissão'}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>
                  Com um usuário escolhido, a conta só entra na extensão se esse usuário tiver a permissão <strong>Usar o BOT GROUP</strong>{' '}
                  (Usuários → permissões) e a empresa tiver o recurso liberado (Empresas → recursos). Cada usuário vincula a uma conta só.
                </Form.Text>
                {usuarioEscolhido && !usuarioEscolhido.botGroup && (
                  <Alert variant="warning" className="small mt-2 mb-0">
                    <strong>{usuarioEscolhido.nome}</strong> ainda <strong>não tem</strong> o BOT GROUP liberado. Se você salvar assim, esta conta
                    ficará bloqueada na extensão (aparece "recurso desabilitado — fale com o administrador") até você liberar a permissão
                    em Usuários (e o recurso na empresa, se não for superadmin).
                  </Alert>
                )}
              </Form.Group>
            )}
            {nova && (
              <Form.Group className="mb-3" controlId="wl-imob">
                <Form.Label>Imobiliária (código)</Form.Label>
                <Form.Select {...campo('imobiliariaId')} required>
                  {imobiliarias.map((i) => (
                    <option key={i.id} value={i.id}>Código {i.codigo}{i.status !== 1 ? ' (inativa)' : ''}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3" controlId="wl-senha">
              <Form.Label>{nova ? 'Senha' : 'Nova senha'}</Form.Label>
              <CampoSenha {...campo('senha')} required={nova} minLength={6} autoComplete="new-password" />
              <Form.Text muted>
                {nova ? 'Mínimo de 6 caracteres.' : 'Deixe em branco para manter a senha atual. Depois de trocar, atualize a senha no app/extensão que usa esta conta.'}
              </Form.Text>
            </Form.Group>
            {!nova && (
              <Form.Group className="mb-1" controlId="wl-status">
                <Form.Label>Situação</Form.Label>
                <Form.Select {...campo('status')}>
                  <option value={1}>Ativa</option>
                  <option value={0}>Inativa</option>
                  <option value={2}>Bloqueada</option>
                </Form.Select>
                <Form.Text muted>Inativa ou bloqueada: o app e a extensão que usam esta conta deixam de conseguir entrar.</Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setModal(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
